'use client'

import { useForm, useWatch } from 'react-hook-form'

import {
  LOCALE_BY_SOURCE_CURRENCY,
  VIRTUAL_ACCOUNT_LABEL,
} from '~/app/settings/team/banks/constants'
import {
  VirtualAccount,
  VirtualAccountDepositInstructions,
} from '~/components/VirtualAccounts'
import {
  Button,
  Copyable,
  Message,
  NetworkLogo,
  SegmentedControl,
  Tabs,
  TabsContent,
} from '~/components/component-library'
import { WalletDepositSection } from '~/components/forms/deposit/wallet/WalletDepositSection'
import { RequestButton } from '~/components/forms/invoice/RequestButton'
import { SupportedChainId } from '~/constants/chains'
import { useActiveOrg, useActiveOrgMember } from '~/hooks/org'
import { useCopyValue } from '~/hooks/useCopyValue'
import { useKycStatus } from '~/hooks/useKycStatus'
import { useNavigation } from '~/hooks/useNavigation'
import { BridgeVirtualAccount, OrgSmartAccount } from '~/types'
import { cn } from '~/utils/tailwind-utils'

const TAB_MIN_HEIGHT = 'min-h-44'
// Matches the wallet tab, whose wallet list makes it taller than the others.
const TAB_MIN_HEIGHT_WITH_WALLET_LIST = 'min-h-60'

export const DepositPanel = ({
  account,
  defaultTab,
  restrictBankToAccount = false,
  showCopyAddressButton = false,
  showWalletDeposit = false,
  showAskForPayment = false,
}: {
  account: OrgSmartAccount
  defaultTab: 'crypto' | 'fiat'
  restrictBankToAccount?: boolean
  // The address field already copies on click, so only the deposit page,
  // where copying is the whole point, adds a button for it.
  showCopyAddressButton?: boolean
  // Both opt in, because they exist for someone funding a treasury for the
  // first time. Everywhere else the panel stays the address and the bank tab.
  showWalletDeposit?: boolean
  showAskForPayment?: boolean
}) => {
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()

  const allVirtualAccounts = org?.bridge?.virtualAccounts ?? []
  const isBankConnected = allVirtualAccounts.length > 0

  const virtualAccounts = restrictBankToAccount
    ? allVirtualAccounts.filter(
        (virtualAccount) =>
          virtualAccount.destinationAddress.toLowerCase() ===
          account.address.toLowerCase(),
      )
    : allVirtualAccounts

  const hideBankTab =
    restrictBankToAccount && isBankConnected && virtualAccounts.length === 0

  // A column, so each tab can pin its action row to the same bottom edge.
  const tabClassName = cn(
    'flex flex-col gap-4',
    showWalletDeposit ? TAB_MIN_HEIGHT_WITH_WALLET_LIST : TAB_MIN_HEIGHT,
  )

  // Only owners can connect or manage a bank (the bank settings page is
  // owner-gated, and non-owners can't onramp), so they get the wallet deposit
  // instructions directly rather than a bank tab that dead-ends on a page they
  // can't access.
  if (orgMember?.role !== 'OWNER' || hideBankTab) {
    return (
      <div className={tabClassName}>
        <DepositFromWalletTab
          account={account}
          showCopyAddressButton={showCopyAddressButton}
          showWalletDeposit={showWalletDeposit}
        />
      </div>
    )
  }

  return (
    <Tabs
      key={defaultTab}
      defaultValue={defaultTab}
      options={[
        { value: 'crypto', display: 'Wallet' },
        {
          value: 'fiat',
          display: 'Bank',
        },
        ...(showAskForPayment
          ? [{ value: 'request', display: 'Request' }]
          : []),
      ]}
    >
      <TabsContent value="crypto" className={tabClassName}>
        <DepositFromWalletTab
          account={account}
          showCopyAddressButton={showCopyAddressButton}
          showWalletDeposit={showWalletDeposit}
        />
      </TabsContent>
      {/* Once the org has a Bridge virtual account, the bank tab shows wire
          details; until then it's the verify-and-connect flow. */}
      <TabsContent value="fiat" className={tabClassName}>
        {isBankConnected ? (
          <ConnectedBankDetails virtualAccounts={virtualAccounts} />
        ) : (
          <ConnectBankPrompt />
        )}
      </TabsContent>
      {showAskForPayment && (
        <TabsContent value="request" className={tabClassName}>
          <AskForPaymentTab account={account} />
        </TabsContent>
      )}
    </Tabs>
  )
}

// Covers money the org expects from someone else — a client, an investor,
// another workspace — rather than a self-deposit.
const AskForPaymentTab = ({ account }: { account: OrgSmartAccount }) => (
  <>
    <p className="text-base text-primary">
      Invoice a client, an investor, or another workspace. They don&apos;t need
      a Splits account to pay.
    </p>
    <div className="mt-auto flex items-center justify-end">
      <RequestButton defaultAccount={account.address} asChild>
        <Button>Create invoice</Button>
      </RequestButton>
    </div>
  </>
)

const DepositFromWalletTab = ({
  account,
  showCopyAddressButton,
  showWalletDeposit,
}: {
  account: OrgSmartAccount
  showCopyAddressButton: boolean
  showWalletDeposit: boolean
}) => {
  const { org } = useActiveOrg()
  const { handleCopy } = useCopyValue()
  const chainIds = ((account.chainIds?.length
    ? account.chainIds
    : org?.smartAccountsChainIds) ?? []) as SupportedChainId[]

  return (
    <>
      <p className="text-base text-primary">
        Send crypto from an existing wallet or exchange.
      </p>
      {showWalletDeposit && (
        <WalletDepositSection account={account} chainIds={chainIds} />
      )}
      <Copyable
        layout="field"
        label={`${account.name} address (EVM)`}
        value={account.address}
        successMessage="Address copied"
        footer={
          chainIds.length > 0 ? (
            <NetworkLogo
              chainIds={chainIds}
              size="sm"
              limit={chainIds.length}
            />
          ) : undefined
        }
      />
      {showCopyAddressButton && (
        <div className="mt-auto flex items-center justify-end">
          <Button onClick={() => handleCopy(account.address, 'Address copied')}>
            Copy address
          </Button>
        </div>
      )}
    </>
  )
}

// Connected state, mirroring OnrampDialog: deposit instructions + wire details.
// When the org has multiple currency accounts, a segmented control switches
// between them — a sub-level control rather than a second row of tabs under the
// panel's own tabs.
const ConnectedBankDetails = ({
  virtualAccounts,
}: {
  virtualAccounts: BridgeVirtualAccount[]
}) => {
  const { control } = useForm({
    defaultValues: { account: virtualAccounts[0].id },
  })
  const selectedId = useWatch({ control, name: 'account' })
  const selected =
    virtualAccounts.find(
      (virtualAccount) => virtualAccount.id === selectedId,
    ) ?? virtualAccounts[0]

  return (
    <div className="space-y-4">
      {virtualAccounts.length > 1 && (
        <SegmentedControl
          control={control}
          inputName="account"
          ariaLabel="Bank account currency"
          options={virtualAccounts.map((virtualAccount) => ({
            value: virtualAccount.id,
            label:
              VIRTUAL_ACCOUNT_LABEL[
                LOCALE_BY_SOURCE_CURRENCY[virtualAccount.sourceData.currency]
              ] ?? virtualAccount.sourceData.currency,
          }))}
        />
      )}
      <VirtualAccountDepositInstructions
        currency={selected.sourceData.currency}
      />
      <VirtualAccount virtualAccount={selected} withForm={false} />
    </div>
  )
}

// Not-connected state: gate on KYC, mirroring the welcome screen / setup dialog.
const ConnectBankPrompt = () => {
  const navigate = useNavigation()
  const { isKycComplete, isKycPending, isKycRejected, isKycStarted } =
    useKycStatus()

  return (
    <>
      <p className="text-base text-primary">
        Deposit from your US or EU bank account by verifying your identity (KYC)
        or business (KYB). Funds typically land in your account within 1
        business day.
      </p>
      {isKycComplete && (
        <Message
          variant="success"
          title="Verification complete"
          action={{
            label: 'Link your bank',
            onClick: () => navigate('/settings/team/banks/'),
          }}
        >
          Link a bank account to start depositing.
        </Message>
      )}
      {isKycPending && (
        <Message
          variant="info"
          title="Verification pending"
          action={{
            label: 'View status',
            onClick: () => navigate('/settings/team/banks/'),
          }}
        >
          Your verification is being reviewed
        </Message>
      )}
      {isKycRejected && (
        <Message variant="danger" title="Verification rejected">
          Email us at support@splits.org for further assistance
        </Message>
      )}
      {!isKycStarted && (
        <div className="mt-auto flex items-center justify-end space-x-4">
          <Button
            variant="tertiary"
            href={{
              path: 'https://splits.org/docs/banking#getting-verified',
              target: 'tab',
            }}
          >
            Learn more
          </Button>
          <Button href={{ path: '/settings/team/banks/' }}>Get started</Button>
        </div>
      )}
    </>
  )
}
